import {
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { createLinkedinMessage } from "../../../../api/ModelsApi";
import {
  IconCheck,
  IconMicrophone,
  IconMicrophoneFilled,
  IconPlayerStopFilled,
  IconTrash,
} from "@tabler/icons-react";
import AudioReactRecorder, { RecordState } from "audio-react-recorder";
import BackButton from "../../../../components/View/BackButton";
import { createFile, getFiles } from "../../../../api/FIleAPI";

let timer;

const LinkedinVoiceRecord = ({ handleClose, onCreated }) => {
  const lang = useLanguage((s) => s.language);
  const theme = useMantineTheme();
  const [voiceFile, setVoiceFile] = useState(null);
  // const [duration, setDuration] = useState(0);
  const [goingDuration, setGoingDuration] = useState(0);

  const [permissionStatus, setPermissionStatus] = useState(false);
  const [recordState, setRecordState] = useState(RecordState.STOP);

  const getPermission = async () => {
    let mediaStream;
    try {
      const constraints = { audio: true };
      mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (ex) {
      if (ex instanceof DOMException) {
        if (ex.name === "NotAllowedError") {
          setPermissionStatus("denied");
        } else if (ex.name === "NotFoundError") {
          setPermissionStatus("not found");
        } else {
          setPermissionStatus("denied");
        }
      } else {
        setPermissionStatus("denied");
      }
    }

    if (mediaStream) {
      setPermissionStatus("granted");
    }
  };

  const startRecord = () => {
    timer = setInterval(() => {
      setGoingDuration((prev) => prev + 100);
    }, 100);
    setVoiceFile(null);
    getPermission().then((res) => setRecordState(RecordState.START));
  };
  const stopRecord = () => {
    setRecordState(RecordState.STOP);
    setGoingDuration(0);
    clearInterval(timer);
  };
  const onStop = (audioData) => {
    // console.log("audioData", audioData);
    setVoiceFile(audioData);
  };

  const handleClear = () => {
    setVoiceFile(null);
  };

  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    let f = new File([voiceFile.blob], "saved_audio.wav", {
      type: "audio/wav",
    });

    setSubmitting(true);
    createFile({
      file: f,
      name: data.name,
    }).then((res) => {
      createLinkedinMessage({
        name: data.name,
        sender: data?.sender,
        file_id: res.data.file_id,
        text: "message",
      })
        .then((r) => {
          handleClose();
          getFiles().then(() => onCreated(r?.data?.model_id));
        })
        .catch(() => setSubmitting(false));
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <LoadingOverlay visible={submitting} />

      <Stack>
        {permissionStatus === "denied" && (
          <Text c="red" mb={4} size="lg">
            {lang.model_smv.smv_editor.dialog.need_permission}
          </Text>
        )}
        {permissionStatus === "not found" && (
          <Group mb={4}>
            <IconMicrophone color="error" />
            <Text c="error" ml={4}>
              {lang.model_smv.smv_editor.dialog.no_mic}
            </Text>
          </Group>
        )}
        <Stack
          pos="relative"
          style={{
            border: "1px solid var(--mantine-color-primary-3)",
            borderRadius: 16,
            borderStyle: "dashed",
          }}
          alignItems="center"
        >
          <AudioReactRecorder
            state={recordState}
            onStop={onStop}
            canvasHeight={200}
            canvasWidth={658}
            foregroundColor={theme.colors.primary[6]}
            backgroundColor={"#fff"}
          />

          <Text fz={18} pos="absolute" top={5} right={10}>
            {(Math.round(goingDuration * 100) / 100000).toFixed(2)}s
          </Text>
        </Stack>

        <Group direction="row" justify="center" gap={"xl"} mb={24}>
          <Tooltip label={lang.model_smv.smv_editor.dialog.save}>
            <ActionIcon
              onClick={startRecord}
              color="primary"
              size={"xl"}
              disabled={
                permissionStatus === "denied" ||
                recordState === RecordState.START
              }
            >
              <IconMicrophoneFilled size={24} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={lang.model_smv.smv_editor.dialog.stop}>
            <ActionIcon
              onClick={stopRecord}
              color="primary"
              size={"xl"}
              disabled={
                permissionStatus === "denied" ||
                recordState === RecordState.STOP
              }
            >
              <IconPlayerStopFilled fontSize="large" />
            </ActionIcon>
          </Tooltip>
        </Group>

        {voiceFile && (
          <>
            <audio
              id="audio"
              controls
              src={voiceFile ? voiceFile.url : null}
              style={{ width: "100%" }}
            ></audio>

            <TextInput
              label={lang.model_emails.create.dialog.name}
              placeholder={lang.model_emails.create.dialog.name}
              name="name"
              w="50%"
              required
              data-autofocus
              autoFocus
              size="md"
            />
          </>
        )}
      </Stack>
      <Group justify="space-between" mt={24}>
        <BackButton
          onClick={handleClose}
          label={lang.model_smv.smv_editor.dialog.close}
          disabled={recordState !== RecordState.STOP}
          size="md"
        />
        <Button
          color="red"
          variant="light"
          onClick={handleClear}
          leftSection={<IconTrash size={18} />}
          ml={"auto"}
          size="md"
          disabled={recordState !== RecordState.STOP || !voiceFile}
        >
          {lang.global.delete}
        </Button>
        <Button
          leftSection={<IconCheck />}
          disabled={recordState !== RecordState.STOP || !voiceFile}
          size="md"
          type="submit"
        >
          {lang.model_smv.smv_editor.dialog.use}
        </Button>
      </Group>
    </form>
  );
};

export default LinkedinVoiceRecord;
